import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { GitPullRequest } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const TASK_TYPES = [
  { value: "taak", label: "Taak" },
  { value: "discipline", label: "Discipline" },
  { value: "rooster", label: "Rooster" },
];
const REQ_TYPES = [
  { value: "toevoegen", label: "Taak toevoegen" },
  { value: "wijzigen", label: "Taak wijzigen" },
  { value: "verwijderen", label: "Taak verwijderen" },
];

const inputCls = "w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900";

export default function WeekritmeRequestDialog({ open, onOpenChange, tasks, onSubmitted }) {
  const [type, setType] = useState("toevoegen");
  const [taskId, setTaskId] = useState("");
  const [day, setDay] = useState("Maandag");
  const [text, setText] = useState("");
  const [taakType, setTaakType] = useState("taak");
  const [motivatie, setMotivatie] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setType("toevoegen"); setTaskId(""); setDay("Maandag"); setText(""); setTaakType("taak"); setMotivatie("");
    }
  }, [open]);

  // Prefill change fields from selected task
  useEffect(() => {
    if (type === "wijzigen" && taskId) {
      const t = tasks.find((x) => x.id === taskId);
      if (t) { setDay(t.day || "Maandag"); setText(t.text || ""); setTaakType(t.type || "taak"); }
    }
  }, [taskId, type, tasks]);

  const submit = async () => {
    const payload = {};
    if (type === "toevoegen") {
      if (!text.trim()) { toast.error("Vul de taaktekst in."); return; }
      payload.day = day; payload.text = text.trim(); payload.type = taakType;
    } else {
      if (!taskId) { toast.error("Kies een bestaande taak."); return; }
      payload.taskId = taskId;
      if (type === "wijzigen") {
        if (!text.trim()) { toast.error("Vul de gewijzigde tekst in."); return; }
        payload.day = day; payload.text = text.trim(); payload.type = taakType;
      }
    }
    setSaving(true);
    try {
      const r = await api.post("/weekritme-requests", { type, payload, motivatie });
      toast.success("Voorstel ingediend");
      onSubmitted && onSubmitted(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Indienen mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto" data-testid="weekritme-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><GitPullRequest className="h-5 w-5 text-blue-600" /> Taak voorstellen</DialogTitle>
          <DialogDescription>Stel voor om een taak in je weekritme toe te voegen, te wijzigen of weg te halen. Oversight beoordeelt je voorstel en past het toe. Iets over de app zelf? Gebruik het wolkje rechtsonder.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Wat wil je voorstellen?</label>
            <select data-testid="req-type" value={type} onChange={(e) => { setType(e.target.value); setTaskId(""); }} className={inputCls}>
              {REQ_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {(type === "wijzigen" || type === "verwijderen") && (
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Bestaande taak</label>
              <select data-testid="req-task" value={taskId} onChange={(e) => setTaskId(e.target.value)} className={inputCls}>
                <option value="">— Kies een taak —</option>
                {tasks.map((t) => <option key={t.id} value={t.id}>{t.day} · {t.text}</option>)}
              </select>
            </div>
          )}

          {(type === "toevoegen" || type === "wijzigen") && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Dag</label>
                  <select data-testid="req-day" value={day} onChange={(e) => setDay(e.target.value)} className={inputCls}>
                    {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Soort</label>
                  <select data-testid="req-taaktype" value={taakType} onChange={(e) => setTaakType(e.target.value)} className={inputCls}>
                    {TASK_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Taaktekst</label>
                <input data-testid="req-text" value={text} onChange={(e) => setText(e.target.value)} className={inputCls} placeholder="Wat moet er gebeuren?" />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Motivatie <span className="text-zinc-400 font-normal">(optioneel)</span></label>
            <textarea data-testid="req-motivatie" rows={2} value={motivatie} onChange={(e) => setMotivatie(e.target.value)} className={inputCls} placeholder="Waarom is dit nodig?" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-medium text-zinc-500 hover:bg-zinc-100">Annuleren</button>
          <button data-testid="req-submit" onClick={submit} disabled={saving}
            className="w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
            {saving ? "Bezig…" : "Voorstel indienen"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_BADGE = {
  open: "bg-amber-100 text-amber-800",
  goedgekeurd: "bg-emerald-100 text-emerald-800",
  afgewezen: "bg-rose-100 text-rose-800",
};
const REQ_TYPE_LABEL = { toevoegen: "Toevoegen", wijzigen: "Wijzigen", verwijderen: "Verwijderen" };

export function VerzoekItem({ req, children, testid }) {
  const p = req.payload || {};
  return (
    <div data-testid={testid} className="rounded-[10px] border border-zinc-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{REQ_TYPE_LABEL[req.type] || req.type}</span>
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_BADGE[req.status] || "bg-zinc-100 text-zinc-500"}`}>{req.status}</span>
      </div>
      <p className="text-sm text-zinc-800">
        {req.type === "verwijderen" ? `Verwijder taak (${p.taskId})` : `${p.day || ""} · ${p.text || ""}`}
        {req.type !== "verwijderen" && p.type && <span className="text-zinc-400"> ({p.type})</span>}
      </p>
      {req.motivatie && <p className="text-xs text-zinc-500 mt-1">Motivatie: {req.motivatie}</p>}
      <p className="text-[11px] text-zinc-400 mt-1">Door {req.requester}</p>
      {req.reviewOpmerking && <p className="text-xs text-zinc-500 mt-1 italic">Opmerking oversight: {req.reviewOpmerking}</p>}
      {children}
    </div>
  );
}
